import ArrowBack from "@app/assets/svgs/ArrowBack";
import Refresh from "@app/assets/svgs/Refresh";
import { logPancakeSwapClicked } from "@app/lib/analytics";
import useConnector from "@app/lib/hooks/useConnector";
import useTokenContract from "@app/lib/hooks/useTokenContract";
import { formatToUSD } from "@app/shared/helpers/currencyHelper";
import { Alert, Skeleton, Snackbar } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import TokenCard from "./TokenCard";

export default function Wallet() {
  const { loading, account } = useConnector();
  const {
    totalDistributedETH,
    getAllContractInformation,
    claimDividend,
    contract,
    walletBalance: balance,
    claimableToken,
    totalDividendsClaimed,
    isLoading,
    isClaiming,
  } = useTokenContract();

  const [alertClaim, setAlertClaim] = useState(false);

  useEffect(() => {
    if (alertClaim) {
      setTimeout(() => {
        setAlertClaim(false);
      }, 2000);
    }
  }, [alertClaim]);

  useEffect(() => {
    if (contract && account) {
      getAllContractInformation(account);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, account]);

  const handleClaim = useCallback(async () => {
    if (Number(claimableToken?.formattedClaimableToken) === 0) {
      setAlertClaim(true);
      return;
    }
    claimDividend();
  }, [claimDividend, claimableToken]);

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  // const isLiquidityEmpty = formatToUSD(balance?.price?.usdt || "0") === "0";

  return (
    <div className="flex flex-col p-4 bg-gray-100 space-y-4">
      <div className="flex items-center space-x-2">
        <div className="font-bold text-base">Wallet Information</div>
        <div
          role="button"
          aria-pressed="false"
          className="text-black transition-color duration-300 hover:text-primary h-6 w-6"
          onClick={() => getAllContractInformation(account)}
          title="Refresh"
        >
          <Refresh />
          <span className="hidden">Refresh</span>
        </div>
      </div>
      <TokenCard
        isLoading={isLoading}
        icon="/images/mtvx-logo.png"
        iconAlt="MTVX Token"
        tokenName="MTVX Tokens"
        tokenValue={formatToUSD(balance?.formattedBalance || "0")}
        leadingText={
          isLoading ? (
            <Skeleton height={16} sx={{ bgcolor: "#e2e2e2" }} width={120} className="rounded" />
          ) : (
            <>
              <div>${formatToUSD(balance?.price?.usdt || "0")}</div>
              <div>{formatToUSD(balance?.price?.bnb || "0")} BNB</div>
            </>
          )
        }
        valueColor="primary"
        actionButton={
          <a
            role="button"
            onClick={logPancakeSwapClicked}
            href="https://pancakeswap.finance/swap?outputCurrency=0xd10672A959CE3BB8939827391fe78762F518f740"
            target="_blank"
            rel="noreferrer noopener"
            className="bg-secondary px-3 py-2 rounded font-medium text-xs md:text-base"
          >
            Buy on Pancake
          </a>
        }
      />
      <TokenCard
        isLoading={isLoading}
        icon="/images/eth.svg"
        iconAlt="ETH"
        tokenName="Claimable ETH"
        tokenValue={claimableToken?.formattedClaimableToken || "0"}
        valueColor="eth"
        actionButton={
          <button
            type="button"
            disabled={isClaiming}
            onClick={handleClaim}
            className={clsx("flex text-center items-center px-3 py-2 rounded font-medium text-xs md:text-base", {
              "cursor-not-allowed": isClaiming,
              "bg-secondary": !isClaiming,
              "bg-gray-300": isClaiming,
            })}
          >
            {isClaiming ? (
              <>
                <CircularProgress size={16} color="secondary" className="mr-2" />
                <span>Claiming rewards..</span>
              </>
            ) : (
              "Claim"
            )}
          </button>
        }
      />
      {alertClaim && (
        <Snackbar open={alertClaim} autoHideDuration={3000} onClose={() => setAlertClaim(false)}>
          <Alert severity="error">Not enough ETH to claim</Alert>
        </Snackbar>
      )}
      <TokenCard
        isLoading={isLoading}
        icon="/images/eth.svg"
        iconAlt="ETH"
        tokenName="Total ETH Claimed"
        tokenValue={totalDividendsClaimed?.formattedAccumulativeDividendOfWallet || "0"}
        valueColor="eth"
      />
      <div className="flex flex-col text-mtvx-eth items-center justify-center bg-[#C9CEE2] min-h-[60px] md:min-h-[81px] border-2 border-[#454A75] rounded">
        <div className="flex items-center space-x-2">
          <img src="/images/eth-token.svg" className="w-7 h-7" alt="ETH" />
          {isLoading ? (
            <Skeleton height={32} width={40} sx={{ bgcolor: "#e2e2e2" }} className="rounded" />
          ) : (
            <div className="font-bold text-2xl">{totalDistributedETH}</div>
          )}
        </div>
        <div className="font-medium">Total Rewards Distributed</div>
      </div>
      <a
        href="https://mtvx.io"
        className="flex items-center rounded self-start border border-primary p-2 space-x-2 text-primary hover:text-white transition-color duration-300 hover:bg-primary"
      >
        <ArrowBack className="w-6 h-6" />
        <span>Back to mtvx.io</span>
      </a>
    </div>
  );
}
